import React, { useContext } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import Tag from "@/components/common/Tag/Tag";
import { AccountIcon } from "@/components/icons/AccountIcon";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import UserContext from "@/context/UserContext";
import { AccordionNavigationItem } from "@/data/navigation/header.data";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useNavigation } from "@/hooks/useNavigation";
import { PromoData } from "@/types/promoData";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

import Accordion from "./Accordion";
import styles from "./AccordionNavigation.module.scss";
import BottomNavigationMobile from "./BottomNavigationMobile";
import PromoSaleBanner from "./PromoSaleBanner";
import StrandSerumNavBanner from "./StrandSerumNavBanner";

interface SubItem {
  type: "link" | "button";
  title?: string;
  subtitle?: string;
  url?: string;
  label?: string;
  name?: string;
  tag?: string;
}

interface Section {
  type: "hgn" | "accordion" | "link";
  name: string;
  title: string;
  open?: boolean;
  url?: string;
  tag?: string;
  children?: SubItem[];
}

interface AccordionNavigationProps {
  data: Section[] | AccordionNavigationItem[];
  toggleNav: (isOpen: boolean) => void;
  navIsOpen: boolean;
  menuIsOpen: boolean;
  promoCf: PromoData;
}

const label = "Shop All Products";

const AccordionNavigation: React.FC<AccordionNavigationProps> = ({
  data,
  toggleNav,
  navIsOpen,
  menuIsOpen,
  promoCf,
}) => {
  const userState = useContext(UserContext);
  const navigate = useNavigation();
  useBodyScrollLock(navIsOpen);

  const trackClicked = (text: string, destination: string | undefined) => {
    const payload = {
      type: "button" as const,
      text,
      destination: destination ?? "",
    };
    GA4.onNavigationClicked(payload);
  };

  const handleLinkClick = (text: string = "", destination?: string, donav = false) => {
    // Delay toggleNav call with function wrapper
    setTimeout(() => toggleNav(false), 50);
    trackClicked(text, destination);
    if (destination && destination !== "#" && donav) {
      navigate(destination);
    }
  };

  const handleQuizClick = (label: string, altDestination?: string) => {
    const destination = altDestination ?? "/hair/brand/quiz/";
    GA4.onMarketingCtaClicked({
      destination: destination,
      module_location: "navigation",
      text: label,
      type: "button",
    });
    setTimeout(() => {
      window.location.href = `${process.env.NEXT_PUBLIC_BASEURL}${destination}`;
    }, 500);
  };

  const generateAccountButton = (isLoggedIn: boolean) => {
    const dest = isLoggedIn ? "/account/" : "/customer/account/login/";
    const label = isLoggedIn ? "My Account" : "Log In";

    return (
      <ButtonNova
        customIcon={<AccountIcon className="w-4" />}
        level="Secondary"
        width="100%"
        label={label}
        icon="Right"
        onClick={() => handleLinkClick(label, dest, true)}
      />
    );
  };

  return (
    <nav className={cn(styles.mobileNavigation, "mobile-navigation")}>
      <div
        className={cn(styles.menuWrapper, "menu-wrapper", {
          [styles.menuIsOpen]: menuIsOpen,
          [styles.navIsOpen]: navIsOpen,
        })}
      >
        {promoCf?.savingsData?.web?.nav?.women ? (
          <PromoSaleBanner data={promoCf?.savingsData?.web?.nav?.women} toggleNav={toggleNav} />
        ) : null}

        {data.map((section, index) => (
          <div key={section.name}>
            {section.type === "hgn" ? (
              <div
                className={cn(
                  styles.productsSection,
                  "bg-nova-base-solid-20 py-6 px-6 m-6 mb-4 flex flex-col md:flex-row md:justify-between md:gap-6"
                )}
              >
                <div className="md:flex-1">
                  <div>
                    <TextElementNova
                      text={section.title}
                      className="Subtitle-10-Dia-Normal text-nova-base-black mb-6"
                    />
                  </div>
                  <div>
                    <div className={section.name}>
                      {section.children?.map((subitem, idx) => (
                        <div
                          key={`${section.name}-${subitem.title ?? idx}`}
                          className={cn({
                            "mb-4": section.children && idx + 1 !== section.children.length,
                          })}
                        >
                          {subitem.type === "link" ? (
                            <LinkHandler
                              onClick={() => handleLinkClick(subitem.title ?? "", subitem.url)}
                              path={subitem.url ?? "#"}
                              aria-label={`Open ${subitem.name ?? ""} page`}
                              id={`SubItemLink-${index}-${idx}`}
                              className="block"
                            >
                              <div className="flex items-center gap-2">
                                <TextElementNova
                                  text={subitem.title ?? ""}
                                  className={cn("Body-20-Dia-Strong text-nova-brand-primary-100")}
                                />
                                {subitem.tag ? (
                                  <Tag
                                    data={{
                                      line: subitem.tag,
                                      classes: "bg-nova-brand-accent-80",
                                    }}
                                  />
                                ) : null}
                              </div>
                              {subitem.subtitle ? (
                                <TextElementNova
                                  text={subitem.subtitle}
                                  className={cn(
                                    "Body-10-Dia-Normal mt-1",
                                    section.name === "products"
                                      ? "text-nova-base-black"
                                      : "text-nova-base-alt-alt"
                                  )}
                                />
                              ) : null}
                            </LinkHandler>
                          ) : null}
                          {subitem.type === "button" ? (
                            <div className="pt-2">
                              <ButtonNova
                                icon="Right"
                                width="100%"
                                label="Shop All Products"
                                className="header-quiz-link"
                                onClick={() =>
                                  handleQuizClick(
                                    subitem.label ?? "",
                                    "/hair/brand/product/category/"
                                  )
                                }
                              />
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <StrandSerumNavBanner
                  className="mt-6 md:mt-0"
                  onNavigate={() => setTimeout(() => toggleNav(false), 50)}
                />
              </div>
            ) : null}

            {section.type === "accordion" ? (
              <Accordion title={section.title} open={section.open ?? false}>
                <div className={cn(styles.accordionBody, "px-4")}>
                  <div className={section.name}>
                    {section.children?.map((subitem, idx) => (
                      <div key={idx}>
                        {subitem.type === "link" ? (
                          <LinkHandler
                            className={cn(
                              styles.subItemLink,
                              { [styles.subItemLinkServices]: section.name === "services" },
                              "block",
                              section.name,
                              `sub-item-link-${subitem.title?.replace(/ /g, "-").toLowerCase()}`
                            )}
                            onClick={() => handleLinkClick(subitem.title ?? "", subitem.url)}
                            path={subitem.url ?? "#"}
                            ariaLabel={`Open ${subitem.title ?? ""} page`}
                            id={`SubItemLink-${index}-${idx}`}
                          >
                            <TextElementNova
                              text={subitem.title ?? ""}
                              className="Body-20-Dia-Normal"
                            />
                            {subitem.subtitle ? (
                              <TextElementNova
                                text={subitem.subtitle}
                                className="Body-10-Dia-Normal pt-1 text-nova-transparency-black-60"
                              />
                            ) : null}
                          </LinkHandler>
                        ) : null}

                        {subitem.type === "button" ? (
                          <ButtonNova
                            width="100%"
                            label={subitem.label ?? ""}
                            onClick={() => handleQuizClick(subitem.label ?? "")}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </Accordion>
            ) : null}

            {section.type === "link" ? (
              <button
                className={cn(styles.menuButton, section.title, {
                  "border-b border-nova-transparency-black-20": section.title !== "Results",
                })}
                onClick={() => handleLinkClick(section.title, section.url, true)}
                aria-label={section.title}
              >
                <div className="flex items-center gap-2">
                  <TextElementNova
                    element="span"
                    text={section.title}
                    className="Body-30-Dia-Normal text-nova-base-black"
                  />
                  {section.tag ? (
                    <Tag
                      data={{
                        line: section.tag,
                        classes: "bg-nova-brand-accent-80",
                      }}
                    />
                  ) : null}
                </div>
              </button>
            ) : null}
          </div>
        ))}

        <div className="pt-4 pb-18">
          <BottomNavigationMobile />
        </div>
      </div>

      <div className={cn(styles.fixedBottomNav, "h-16", "px-4", "py-2")}>
        <div className="w-1/2 pr-1">{generateAccountButton(userState.isLoggedIn)}</div>
        <div className={cn(styles.fixedBottomNavProducts, "w-1/2 pl-1")}>
          <ButtonNova
            level="Secondary"
            icon="Right"
            width="100%"
            label={label}
            onClick={() => handleLinkClick(label, "/hair/brand/product/category/", true)}
          />
        </div>
      </div>
    </nav>
  );
};

export default AccordionNavigation;
